import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { ArrowLeft, Check } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreateReseller, type CreateResellerPayload } from '@/api/resellers';
import { CustomerSearchSelect } from '@/pages/contracts/new/customer-search-select';

interface ResellerFormValues {
  customer_id: number;
  reseller_name: string;
  reseller_tier: string;
}

const TIER_OPTIONS = [
  { label: 'Bronze', value: 'bronze' },
  { label: 'Silver', value: 'silver' },
  { label: 'Gold', value: 'gold' },
  { label: 'Platinum', value: 'platinum' },
];

/**
 * `/resellers/new` — create a new reseller. Fields:
 * - customer_id (async Combobox search, required)
 * - reseller_name (text, required)
 * - reseller_tier (select, optional)
 *
 * Supports pre-fill from location state (customer_id, reseller_name) for the
 * Candidates tab "Create Reseller" quick-action flow.
 */
export default function ResellerNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const prefill = (location.state ?? {}) as Partial<ResellerFormValues>;

  const create = useCreateReseller();
  const [submitError, setSubmitError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<ResellerFormValues>({
    defaultValues: {
      customer_id: prefill.customer_id ?? 0,
      reseller_name: prefill.reseller_name ?? '',
      reseller_tier: '',
    },
  });

  const customerId = watch('customer_id');

  const onSubmit = async (values: ResellerFormValues) => {
    if (!values.customer_id) return;
    setSubmitError('');
    try {
      const payload: CreateResellerPayload = {
        customer_id: values.customer_id,
        reseller_name: values.reseller_name.trim(),
        reseller_tier: values.reseller_tier || undefined,
      };
      await create.mutateAsync(payload);
      toast.success('Reseller created');
      navigate('/resellers');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred.';
      setSubmitError(message);
      toast.error('Failed to create reseller', { description: message });
    }
  };

  return (
    <Container size="lg">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader
          title="Add Reseller"
          description="Create a new reseller linked to an existing customer account."
          actions={
            <Button
              variant="ghost"
              startIcon={<ArrowLeft size={15} />}
              onClick={() => navigate('/resellers')}
            >
              Back
            </Button>
          }
        />

        {submitError ? (
          <Alert tone="danger" title="Failed to create reseller">{submitError}</Alert>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              {/* Customer picker — async Combobox */}
              <CustomerSearchSelect
                value={customerId}
                onChange={(id) => setValue('customer_id', id, { shouldValidate: true })}
                label="Customer"
                required
                error={errors.customer_id ? 'Customer is required' : undefined}
              />

              {/* Reseller name */}
              <Controller
                control={control}
                name="reseller_name"
                rules={{ required: 'Reseller name is required' }}
                render={({ field }) => (
                  <Field label="Reseller Name" required error={errors.reseller_name?.message}>
                    {(p) => (
                      <Input
                        {...p}
                        placeholder="Enter reseller name"
                        invalid={!!errors.reseller_name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  </Field>
                )}
              />

              {/* Tier */}
              <Controller
                control={control}
                name="reseller_tier"
                render={({ field }) => (
                  <Field label="Tier">
                    {(p) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger {...p} onBlur={field.onBlur}>
                          <SelectValue placeholder="Select tier (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIER_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                )}
              />
            </CardBody>
          </Card>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--vn-space-2)',
              marginTop: 'var(--vn-space-4)',
            }}
          >
            <Button
              variant="secondary"
              onClick={() => navigate('/resellers')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              startIcon={<Check size={15} />}
              loading={isSubmitting}
              disabled={!customerId || isSubmitting}
            >
              Create Reseller
            </Button>
          </div>
        </form>
      </div>
    </Container>
  );
}
